import React, { useState } from 'react'
import Popup from '../../components/Popup'
import RequestBillPopupV2 from '../Profile/RequestBillPopupV2'
import { httpClient } from '../../funcs'
import { notifyAboutError } from '../../methods/notifyUser'

type Props = {
    onClose: () => void
    tariff: any
    subscriptionExpiresAt?: string | null
    subscriptionWontRenew: boolean
    cards: any[]
    cardsLoading: boolean
    cancelLoading: boolean
    renewalInvoiceAlreadyRequested: boolean
    renewalItems: any[]
    $store: any
    $commitToStore: (data: any) => void
    onLoadTariffs?: () => void
    onCancelClick: () => void
    onRenewalBillCreated: () => void
}

function formatRubFromMinor(price: unknown): string {
    const n = typeof price === 'number' ? price : Number(price)
    if (!Number.isFinite(n)) {
        return '—'
    }
    return `${(n / 100).toLocaleString('ru-RU', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2,
    })} ₽`
}

function subscriptionPeriodHint(period?: string): string {
    if (period === 'month') return 'за месяц'
    if (period === 'year') return 'за год'
    if (period === 'week') return 'за неделю'
    return ''
}

function formatRuDate(iso: string): string {
    try {
        const d = new Date(iso)
        return d.toLocaleDateString('ru-RU', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        })
    } catch {
        return iso
    }
}

function formatCardExpiryDate(raw: unknown): string {
    const value = String(raw ?? '').trim()
    if (/^\d{4}$/.test(value)) {
        return `${value.slice(0, 2)}/${value.slice(2)}`
    }
    return value
}

function getCardTitle(card: any): string {
    return (
        card?.Pan ||
        card?.pan ||
        card?.masked_pan ||
        card?.card_pan ||
        card?.CardNumber ||
        card?.card_number ||
        'Привязанная карта'
    )
}

function getCardSubtitle(card: any): string {
    const expiryDate =
        card?.ExpDate ||
        card?.exp_date ||
        card?.expiry_date ||
        card?.expiration_date
    const cardId = card?.CardId || card?.card_id || card?._id

    if (expiryDate) {
        return `Срок действия: ${formatCardExpiryDate(expiryDate)}`
    }
    if (cardId) {
        return `ID карты: ${cardId}`
    }
    return 'Используется для регулярных списаний'
}

function SubscriptionArchiveInfoPopup({
    onClose,
    tariff,
    subscriptionExpiresAt,
    subscriptionWontRenew,
    cards,
    cardsLoading,
    cancelLoading,
    renewalInvoiceAlreadyRequested,
    renewalItems,
    $store,
    $commitToStore,
    onLoadTariffs,
    onCancelClick,
    onRenewalBillCreated,
}: Props) {
    const [showBillPopup, setShowBillPopup] = useState(false)
    const [changeCardLoading, setChangeCardLoading] = useState(false)
    const actualCard = cards[0]
    const period = subscriptionPeriodHint(tariff?.period)

    const handleChangeCard = async () => {
        if (changeCardLoading) {
            return
        }
        setChangeCardLoading(true)
        try {
            const { data: result } = await httpClient.post('/add_payment_method')
            const paymentUrl = result?.PaymentURL ?? result?.paymentUrl
            if (paymentUrl) {
                window.location.href = paymentUrl
                return
            }
            notifyAboutError({ $store, $commitToStore }, new Error('Не удалось открыть форму банка для новой карты'))
        } catch (error) {
            notifyAboutError({ $store, $commitToStore }, error)
        } finally {
            setChangeCardLoading(false)
        }
    }

    return (
        <Popup onClose={onClose} maxWidth="560px">
            <>
                <div className="subscription-archive-popup">
                    <h3 className="subscription-archive-title">
                        Безлимитные подписки больше не продаются
                    </h3>
                    <div className="subscription-archive-text">
                        Ваш тариф находится в архиве, но продолжает работать до конца оплаченного периода. Вы можете
                        отменить автопродление, однако вернуться на эту подписку после отмены уже не получится.
                    </div>

                    <div className="subscription-renewal-section">
                        <div className="subscription-section-title">Продление подписки</div>
                        <div className="subscription-renewal-price">
                            Стоимость продления:{' '}
                            <strong>{formatRubFromMinor(tariff?.price)}</strong>
                            {period ? ` ${period}` : ''}
                        </div>
                        {tariff?.subscription_price_level_change && (
                            <div className="subscription-renewal-hint">
                                Стоимость безлимитного тарифа изменилась
                                {tariff?.base_price ? ` с ${formatRubFromMinor(tariff.base_price)}` : ''}.
                            </div>
                        )}
                        {subscriptionExpiresAt && (
                            <div className="subscription-renewal-hint">
                                Оплаченный период до {formatRuDate(subscriptionExpiresAt)}
                            </div>
                        )}
                        <button
                            type="button"
                            className={`new action-button subscription-renewal-button ${
                                renewalInvoiceAlreadyRequested ? 'secondary' : 'primary'
                            }`}
                            onClick={() => setShowBillPopup(true)}
                            disabled={renewalInvoiceAlreadyRequested}
                        >
                            <span className="button-text">
                                {renewalInvoiceAlreadyRequested ? 'Счёт запрошен' : 'Запросить счёт на продление'}
                            </span>
                        </button>
                        {!renewalInvoiceAlreadyRequested && (
                            <div className="subscription-renewal-footer-note">
                                Счёт сформируется сразу и будет отправлен на email из профиля.
                            </div>
                        )}
                    </div>

                    <div className="subscription-card-section">
                        <div className="subscription-section-title">Карта для списаний</div>
                        {cardsLoading && (
                            <div className="subscription-card-placeholder">Загружаем карту...</div>
                        )}
                        {!cardsLoading && actualCard && (
                            <div className="subscription-payment-card">
                                <div className="subscription-payment-card-title">{getCardTitle(actualCard)}</div>
                                <div className="subscription-payment-card-subtitle">{getCardSubtitle(actualCard)}</div>
                            </div>
                        )}
                        {!cardsLoading && !actualCard && (
                            <div className="subscription-card-placeholder">
                                Не нашли подтвержденную карту для автосписаний.
                            </div>
                        )}
                        <button
                            type="button"
                            className="new action-button secondary subscription-change-card-button"
                            onClick={handleChangeCard}
                            disabled={changeCardLoading}
                        >
                            <span className="button-text">
                                {changeCardLoading
                                    ? 'Открываем банк...'
                                    : actualCard
                                        ? 'Сменить карту'
                                        : 'Привязать карту'}
                            </span>
                        </button>
                        <div className="subscription-renewal-footer-note">
                            Банк спишет 1 ₽ для проверки карты и вернёт их. Следующее списание подписки пойдёт с новой карты.
                        </div>
                    </div>

                    <button
                        type="button"
                        className="new action-button secondary subscription-cancel-button"
                        onClick={onCancelClick}
                        disabled={cancelLoading || subscriptionWontRenew}
                    >
                        <span className="button-text">
                            {subscriptionWontRenew ? 'Подписка уже отменена' : 'Отменить подписку'}
                        </span>
                    </button>
                </div>
                {showBillPopup && (
                    <RequestBillPopupV2
                        close={() => setShowBillPopup(false)}
                        onCollectBonus={() => {}}
                        onBillCreated={onRenewalBillCreated}
                        onLoadTariffs={onLoadTariffs}
                        onPaymentProofUploaded={() => setShowBillPopup(false)}
                        $store={$store}
                        $commitToStore={$commitToStore}
                        items={renewalItems}
                    />
                )}
            </>
        </Popup>
    )
}

export default SubscriptionArchiveInfoPopup
